import React from "react";

import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import { ArrowUpRight, MapPin } from "lucide-react";

import { CopyButton } from "@/components/common/copy-button";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { makeTooltip } from "@/components/ui/tooltip";
import {
  VENUE_AUDIT_KIND_HINT,
  venueAuditCidHref,
  venueAuditFindingMetric,
  venueAuditKindLabel,
  venueAuditVenueDisplayName,
  venueAuditVenueHref,
} from "@/lib/tina/venue-audit";
import { TwinEventPairs } from "@/pages/tina/audits/venue/components/twin-event-pairs";
import type { VenueAuditFindingRow, VenueAuditKind } from "@/types/tina";

export interface UseVenueAuditColumnsOptions {
  /** Opens the dismiss dialog for one finding. */
  onDismiss: (row: VenueAuditFindingRow) => void;
  /** The finding currently being dismissed, so its button can go quiet. */
  dismissingId: string | null;
}

/**
 * Columns for the Venue tab of `/tina/audits`.
 *
 * **Nothing here carries a column filter.** `GET /venue-audit/findings` filters on exactly
 * three params — `status`, `kind`, `severity` — and all three are owned by the page's own
 * toolbar, whose values go in as `permanent` and would override anything a column filter
 * set. Same honesty the refresh-jobs columns keep: an inert control is worse than none.
 * Sorting is off for the same reason: the route has no `sort` param, the order is fixed at
 * `severity ASC, first_seen_at DESC` — which puts `high` above `low` — and a header that
 * looked sortable would silently do nothing.
 *
 * The **Finding** column is the whole page. It names every venue with its address and links
 * each to its IMS page, because for a `cid_collision` the distance alone cannot separate
 * "one club, renamed" from "two different bars": pairs at 24 m and 45 m in the real data
 * are genuinely different businesses while pairs at 0 m are the same place spelled twice.
 * The CID, when the finding has one, links to the single Google place both records claim.
 */
export const useVenueAuditColumns = ({
  onDismiss,
  dismissingId,
}: UseVenueAuditColumnsOptions) =>
  React.useMemo<ColumnDef<VenueAuditFindingRow>[]>(
    () => [
      {
        id: "severity",
        accessorKey: "severity",
        header: "Severity",
        size: 90,
        cell: ({ row }) =>
          makeTooltip(
            <Badge
              variant={
                row.original.severity === "high" ? "destructive" : "secondary"
              }
              className="text-[10px] uppercase"
            >
              {row.original.severity}
            </Badge>,
            row.original.kind === "venue_twin"
              ? row.original.severity === "high"
                ? "Within 50 m — users see the same event twice, most likely one place held as two records"
                : "50 to 150 m apart — often a room and its building, or two real neighbours"
              : row.original.severity === "high"
                ? "Provably wrong, or too far apart to be one place"
                : "Same building, most likely one place under two spellings — still worth a look",
          ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "kind",
        accessorKey: "kind",
        header: "What",
        size: 170,
        cell: ({ row }) => {
          const kind = row.original.kind;
          const hint = VENUE_AUDIT_KIND_HINT[kind as VenueAuditKind];
          return makeTooltip(
            <Badge variant="outline" className="text-[10px] font-normal">
              {venueAuditKindLabel(kind)}
            </Badge>,
            // An orchestrator deploy can ship a seventh check before this build
            // knows it; the raw kind plus the fingerprint is still readable.
            hint ?? row.original.fingerprint,
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "finding",
        header: "Finding",
        size: 560,
        cell: ({ row }) => {
          const finding = row.original;
          const venues = finding.evidence.venues ?? [];
          const metric = venueAuditFindingMetric(finding);
          const cidHref = venueAuditCidHref(finding.evidence.cid);
          return (
            <div className="flex flex-col gap-0.5 py-1">
              {venues.length === 0 ? (
                <span className="font-mono text-xs">{finding.fingerprint}</span>
              ) : (
                venues.map((venue) => {
                  const displayName = venueAuditVenueDisplayName(
                    venue,
                    finding,
                  );
                  return (
                    <div
                      key={venue.venue_id}
                      className="flex items-baseline gap-1.5"
                    >
                      <a
                        href={venueAuditVenueHref(venue.venue_id)}
                        className="group inline-flex shrink-0 items-center gap-1 text-xs font-medium hover:underline"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {displayName}
                        <ArrowUpRight className="h-3 w-3 shrink-0 text-muted-foreground" />
                      </a>
                      {venue.cid_role !== "none" ? (
                        <span className="shrink-0 text-[10px] uppercase text-muted-foreground">
                          {venue.cid_role}
                        </span>
                      ) : null}
                      <span className="truncate text-xs text-muted-foreground">
                        {venue.address || "no address"}
                      </span>
                    </div>
                  );
                })
              )}
              <div className="flex items-center gap-2">
                {metric ? (
                  <span className="truncate text-xs text-muted-foreground">
                    {metric}
                  </span>
                ) : null}
                {cidHref ? (
                  <a
                    href={cidHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex shrink-0 items-center gap-1 font-mono text-[10px] text-muted-foreground hover:underline"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <MapPin className="h-3 w-3" />
                    {finding.evidence.cid}
                  </a>
                ) : null}
              </div>
              {finding.kind === "venue_twin" ? (
                <TwinEventPairs evidence={finding.evidence} />
              ) : null}
            </div>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "venue_ids",
        header: "Venue ID",
        size: 190,
        // Every repair path — Firestore, a cleanup script, a ticket — starts with the id,
        // and until now it only existed inside the evidence blob. Shown in full rather
        // than truncated: a curator pastes this elsewhere, and a truncated id invites
        // pasting the wrong one. Monospace and narrow so a 20-character opaque string
        // does not blow out the column the way it would at the table's normal text size.
        cell: ({ row }) => {
          const venues = row.original.evidence.venues ?? [];
          if (venues.length === 0) return null;
          return (
            <div className="flex flex-col gap-1 py-1">
              {venues.map((venue) => (
                <div key={venue.venue_id} className="flex items-center gap-1">
                  <span className="whitespace-nowrap font-mono text-[10px] text-muted-foreground">
                    {venue.venue_id}
                  </span>
                  <CopyButton
                    text={venue.venue_id}
                    message="Venue ID copied"
                    className="h-6 w-6 shrink-0 p-0"
                  />
                </div>
              ))}
            </div>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "reach",
        header: "Reach",
        size: 130,
        // What this bind has ALREADY cost, then what it keeps costing.
        //
        // The published-event count leads because it is the number that makes a finding
        // worth a curator's afternoon: the placeholder family is "121 cache rows", which
        // sounds like a tidy-up, and "796 published events", which is the actual damage.
        // The cache figure below it is the forward-looking half — how many keys keep
        // replaying the bind onto new events.
        //
        // Each half is dashed independently, never zeroed. The two CID-keyed kinds name
        // several venues so tina cannot attribute one event count to them, and the two
        // catalog-only checks never touch the cache — a literal "0 events" there would
        // read as "harmless", which is a different claim from "not counted".
        cell: ({ row }) => {
          const {
            cache_rows: rows = 0,
            distinct_sites: sites = 0,
            published_events: events = 0,
          } = row.original.evidence;
          const dash = <span className="text-xs text-muted-foreground">—</span>;
          return (
            <div className="flex flex-col gap-0.5">
              {events === 0
                ? dash
                : makeTooltip(
                    <span className="font-mono text-xs tabular-nums">
                      {events} {events === 1 ? "event" : "events"}
                    </span>,
                    `${events} published Mood events are bound to this venue and inherited this bind. A floor, not a total: events published without a recorded venue resolution are not counted.`,
                  )}
              {rows === 0
                ? null
                : makeTooltip(
                    <span className="font-mono text-[10px] tabular-nums text-muted-foreground">
                      {rows} / {sites} cached
                    </span>,
                    `${rows} cache rows across ${sites} sites bind to this venue, so future events keep inheriting it`,
                  )}
            </div>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "first_seen_at",
        accessorKey: "first_seen_at",
        header: "First seen",
        size: 110,
        cell: ({ row }) =>
          makeTooltip(
            <span className="font-mono text-xs tabular-nums">
              {dayjs(row.original.first_seen_at).format("DD/MM HH:mm")}
            </span>,
            `last seen ${dayjs(row.original.last_seen_at).format("DD/MM/YYYY HH:mm")}`,
          ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "actions",
        header: "",
        size: 120,
        // Dismissal is the only action this page has, and it is not reversible
        // from here — so a row that already carries one shows who did it instead
        // of a button that would look like it could be undone.
        cell: ({ row }) => {
          const finding = row.original;
          if (finding.dismissed_at) {
            return makeTooltip(
              <span className="text-xs text-muted-foreground">Dismissed</span>,
              `${dayjs(finding.dismissed_at).format("DD/MM/YYYY HH:mm")} by ${finding.dismissed_by ?? "unknown"}${
                finding.dismissed_note ? ` — ${finding.dismissed_note}` : ""
              }`,
            );
          }
          if (finding.resolved_at) {
            return makeTooltip(
              <span className="text-xs text-muted-foreground">Fixed</span>,
              `The audit stopped seeing this on ${dayjs(finding.resolved_at).format("DD/MM/YYYY HH:mm")}`,
            );
          }
          return (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              disabled={dismissingId === finding.id}
              onClick={(event) => {
                event.stopPropagation();
                onDismiss(finding);
              }}
            >
              Dismiss
            </Button>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    [onDismiss, dismissingId],
  );
